import { useState, useCallback } from "react";
import { Copy, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Iter 49.0 — Minimal clipboard-copy affordance for natural-key labels.
 *
 * Renders a tiny inline button that copies the given code to the clipboard
 * and surfaces a 1-second confirmation. Per operator directive (2026-02):
 *   • no additional state complexity beyond a 1-second "copied" flag,
 *   • no modal flows,
 *   • no redesign of surrounding surface,
 *   • operator-readable feedback.
 *
 * Used on Clients / Leads / Catalogue list rows next to the surfaced
 * natural-key (client_code / lead_code / code) so operators can quickly
 * grab the value for socialisation with finance / external integrators.
 */
export function CopyCodeButton({ code, label = "code", testid }) {
    const [copied, setCopied] = useState(false);

    const onCopy = useCallback(
        async (e) => {
            // Stop the row's onClick (e.g., the row's Link to client ledger)
            // from firing when the operator clicks the copy icon.
            e.stopPropagation();
            e.preventDefault();
            try {
                await navigator.clipboard.writeText(code);
                setCopied(true);
                toast.success(`${label} copied — ${code}`);
                setTimeout(() => setCopied(false), 1000);
            } catch {
                toast.error("Clipboard not available — copy the value manually");
            }
        },
        [code, label],
    );

    if (!code) return null;

    return (
        <button
            type="button"
            onClick={onCopy}
            className="ml-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 transition-colors align-middle"
            title={`Copy ${label} to clipboard`}
            aria-label={`Copy ${label} ${code}`}
            data-testid={testid}
        >
            {copied
                ? <Check size={10} weight="bold" className="text-emerald-600" />
                : <Copy size={10} weight="regular" />}
        </button>
    );
}

export default CopyCodeButton;
